import { Component, computed, inject, input, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { TabComponent } from '../../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../../components/tab-layout/tab-layout.component';
import { APITableComponent } from '../../../../components/table/api-table/api-table.component';
import { TableRow } from '../../../../components/table/table.interface';
import { ModalComponent } from '../../../../components/modal/modal.component';
import { PieComponent } from '../../../../components/pie/pie.component';
import { ChartData } from '../../../../interfaces/global/chart';
import {
  CustomerCallAnalytics,
  CustomerCallSection,
} from '../../../../interfaces/apis/customer-calls-analytics';
import { VisitsService } from '../../../../services/visits.service';
import { ConfigService } from '../../../../services/config.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { InlineLoaderComponent } from '../../../../components/inline-loader/inline-loader.component';

interface CustomerCallAnalyticsCard {
  id: string;
  dateLabel: string;
  product: string;
  duration: string;
  objection: string;
  remarks: string;
  chartData: ChartData;
}

@Component({
  selector: 'app-customer-visits-tab',
  imports: [
    TabLayoutComponent,
    APITableComponent,
    ModalComponent,
    PieComponent,
    InlineLoaderComponent,
  ],
  providers: [
    { provide: TabComponent, useExisting: CustomerVisitsTabComponent },
  ],
  templateUrl: './customer-visits-tab.component.html',
  styleUrl: './customer-visits-tab.component.scss',
})
export class CustomerVisitsTabComponent extends TabComponent {
  private visitsService = inject(VisitsService);
  private configService = inject(ConfigService);
  private snackbarService = inject(SnackbarService);

  override name = this.dictionaryService.get('CustomerVisits');

  userId = input.required<string>();
  endpoint = computed(
    () => `/visits/tables/customers-visit-rate/${this.userId()}`,
  );

  isAnalyticsModalOpen = signal<boolean>(false);
  isAnalyticsLoading = signal<boolean>(false);
  selectedCustomerName = signal<string>('');
  callsAnalytics = signal<CustomerCallAnalyticsCard[]>([]);

  public async main(): Promise<void> {}

  openCallsAnalytics = async (row: TableRow): Promise<void> => {
    const customerId = this.readStringValue(row, 'customerId');

    if (!customerId) {
      this.snackbarService.showError('FailedToLoadData');
      return;
    }

    const customerName =
      this.readStringValue(row, 'Name') ||
      this.readStringValue(row, 'name') ||
      this.dictionaryService.get('CustomerVisits');

    this.selectedCustomerName.set(customerName);
    this.isAnalyticsLoading.set(true);

    const from = this.configService.from();
    const to = this.configService.to();

    try {
      const response = await lastValueFrom(
        this.visitsService.getCustomerCallsAnalytics(
          this.userId(),
          customerId,
          from,
          to,
        ),
      );

      const cards = (response.data?.calls || []).map((call, index) =>
        this.mapCallToCard(call, index),
      );

      this.callsAnalytics.set(cards);
      this.isAnalyticsModalOpen.set(true);
    } catch {
      this.snackbarService.showError('FailedToLoadData');
    } finally {
      this.isAnalyticsLoading.set(false);
    }
  };

  onModalClose = (): void => {
    this.isAnalyticsModalOpen.set(false);
    this.callsAnalytics.set([]);
    this.selectedCustomerName.set('');
  };

  private mapCallToCard(
    call: CustomerCallAnalytics,
    index: number,
  ): CustomerCallAnalyticsCard {
    const sections = call.sections || [];

    return {
      id: `${call.date}-${index}`,
      dateLabel: (call.date || '').trim() || '-',
      product: call.product || '-',
      duration: call.duration || '-',
      objection: (call.objection || '').trim() || '-',
      remarks: (call.remarks || '').trim() || '-',
      chartData: this.buildChartData(sections),
    };
  }

  private buildChartData(sections: CustomerCallSection[]): ChartData {
    return {
      chartLabel: this.dictionaryService.get('Duration') || 'Duration',
      itemLabels: sections.map((section) => section.name),
      itemValues: sections.map((section) => section.Precentage || 0),
      itemDisplayValues: sections.map((section) => section.duration || '-'),
    };
  }

  private formatDuration(totalSeconds: number): string {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private readStringValue(row: TableRow, key: string): string {
    const value = row.value[key];

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }

    return '';
  }
}
